import { Box, Button, Flex, Text, useMediaQuery, VStack, Link, Image } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';

import FormField from 'components/FormField';
import { FetchUserDetails, SetUserModalVisible, UpdateUserDetails, SetTabs } from 'redux/UserSlice';
import { SignUpUser } from 'models/UsersSlice.interface';
import { UserDetailsSchema } from 'validation/schemas/UserManagementSchema';
import Lock from 'components/Icons/Lock';
import { handleFileUpload } from 'services/s3Service';
import { acceptedFormats } from 'utils/FileFormats';
import UserBorderNew from 'components/Icons/UserBorderIconNew';

import UserShimmer from '../UserManagementShimmer';

interface Properties {
  userData: any;
  updateUserDetail: (data: SignUpUser) => Promise<void>;
  getUserDetails: (data: any) => Promise<void>;
  setUserModalVisible: (data: boolean) => void;
  setUserTabs: (tab: number) => void;
}

const ProfileFormNew = ({
  getUserDetails,
  updateUserDetail,
  userData,
  setUserModalVisible,
  setUserTabs,
}: Properties) => {
  const [isSmallLaptop] = useMediaQuery('(max-width: 768px)');
  const [isLaptop] = useMediaQuery('(max-width: 1024px)');
  const [isPasswordChangeVisible, setPasswordChangeVisibility] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [loader, setLoader] = useState(true);

  useEffect(() => {
    const getUserDetail = async () => {
      await getUserDetails({});
      setLoader(false);
    };
    getUserDetail();
  }, []);

  const handleSubmit = async (form: any, setSubmitting: (data: boolean) => void) => {
    await updateUserDetail({ request_body: form, toaster: true });
    setSubmitting(false);
  };

  const handleImageUpload = (data: string) => {
    updateUserDetail({
      request_body: { profile_image_url: data },
      toaster: {
        successMessage: 'Image Uploaded Successfully',
      },
    });
  };

  const handleImageDrop = async (file: any, setFieldValue: any) => {
    setImageLoading(true);
    const data = await handleFileUpload(file[0], 'blox-user-profile');
    setImageLoading(false);
    setFieldValue('profile_image_url', data);
    handleImageUpload(data);
  };

  const handleClose = () => {
    setUserModalVisible(false);
    setUserTabs(0);
  };

  if (loader) return <UserShimmer />;
  return (
    <Flex width={'100%'} height={'100%'} flexDir='column' bg='white'>
      <Formik
        initialValues={{
          firstname: userData?.firstname || '',
          lastname: userData?.lastname || '',
          job_role: userData?.job_role || '',
          username: userData?.username || '',
          profile_image_url: userData?.profile_image_url || '',
          current_password: '',
          new_password: '',
          confirmPassword: '',
        }}
        onSubmit={(form, { setSubmitting }) => {
          handleSubmit(form, setSubmitting);
        }}
        validationSchema={UserDetailsSchema}
      >
        {({ errors, touched, values, setFieldValue, isSubmitting, submitCount }: any) => {
          const passwordRules = [
            { label: 'Minimum 8 characters', isMet: values.new_password.length >= 8 },
            { label: 'At least 1 number', isMet: /\d/.test(values.new_password) },
            { label: 'At least 1 uppercase letter', isMet: /[A-Z]/.test(values.new_password) },
            { label: 'At least 1 lowercase letter', isMet: /[a-z]/.test(values.new_password) },
            {
              label: 'At least 1 symbol',
              isMet: /[^\dA-Za-z]/.test(values.new_password),
            },
          ];
          const showUnmetPasswordRulesInRed = submitCount > 0;

          return (
            <Form>
              <Box height={'100%'} width={'100%'} px={2}>
                {/* Header Section */}
                <Flex
                  width={'100%'}
                  justifyContent={'space-between'}
                  alignItems={'flex-start'}
                  mb={4}
                >
                  <Box>
                    <Text
                      fontFamily='Comfortaa bold'
                      color='#212121'
                      fontSize={isLaptop ? '16px' : '20px'}
                      fontWeight='bold'
                      mb='2'
                    >
                      Profile
                    </Text>
                    <Text color='#787878' fontSize='14px' fontFamily='regular_roboto'>
                      Update your personal information
                    </Text>
                  </Box>
                </Flex>

                <Box
                  width={'100%'}
                  height={`calc(100% - ${isLaptop ? '120px' : '140px'})`}
                  overflowY='auto'
                  px={2}
                  pl={2}
                >
                  {/* Profile Image Section - Above */}
                  <Flex
                    width={'100%'}
                    // flexDirection={''}
                    alignItems={'flex-start'}
                    mb={isLaptop ? '6' : '8'}
                  >
                    {values.profile_image_url ? (
                      <Image
                        boxShadow={'1px 1px 10px #d9d9d9'}
                        src={values.profile_image_url}
                        borderRadius={'50%'}
                        width={'66px'}
                        height={'66px'}
                        mb='4'
                        objectFit='cover'
                      />
                    ) : (
                      <UserBorderNew />
                    )}
                    <VStack spacing='2' alignItems={'flex-start'} pt={2} pl={2}>
                      <Dropzone
                        onDrop={(file) => handleImageDrop(file, setFieldValue)}
                        multiple={false}
                        disabled={isSubmitting || imageLoading}
                        accept={acceptedFormats}
                      >
                        {({ getRootProps, getInputProps }) => (
                          <Box
                            {...getRootProps()}
                            cursor={isSubmitting || imageLoading ? 'not-allowed' : 'pointer'}
                            opacity={isSubmitting || imageLoading ? 0.5 : 1}
                          >
                            <input {...getInputProps()} />
                            <Link
                              color='#6B5FC6'
                              textDecoration='underline'
                              fontSize='16px'
                              fontWeight='600'
                              fontFamily='regular_roboto'
                              _hover={{ textDecoration: 'underline' }}
                            >
                              Remove or upload new
                            </Link>
                          </Box>
                        )}
                      </Dropzone>
                      <Text
                        color='#6A6A6A'
                        fontSize='14px'
                        fontWeight='400'
                        fontFamily='regular_roboto'
                        textAlign='left'
                      >
                        Professional headshot recommended (JPG, PNG up to 5MB)
                      </Text>
                    </VStack>
                  </Flex>

                  {/* Form Fields Section - Below Image */}
                  <Box width={'100%'}>
                    <VStack
                      spacing='5'
                      alignItems={'flex-start'}
                      width={'100%'}
                      fontFamily='regular_roboto'
                    >
                      {/* First Name and Last Name Row */}
                      <Flex
                        alignItems={'flex-start'}
                        gap='5'
                        width={'100%'}
                        flexDir={isSmallLaptop ? 'column' : 'row'}
                      >
                        <FormField
                          label='First name'
                          name='firstname'
                          type='text'
                          labelStyles={{
                            fontSize: '12px',
                            color: '#82736C',
                            fontWeight: '600',
                          }}
                          inputStyles={{
                            maxWidth: '100%',
                            boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                            height: '35px',
                            paddingInlineStart: '8px',
                            fontWeight: '500',
                            color: '#212121',
                          }}
                          placeholder='Enter first name'
                          error={errors.firstname}
                          touched={touched.firstname}
                          disabled={isSubmitting}
                        />
                        <FormField
                          label='Last name'
                          name='lastname'
                          type='text'
                          labelStyles={{
                            fontSize: '12px',
                            color: '#82736C',
                            fontWeight: '600',
                          }}
                          inputStyles={{
                            maxWidth: '100%',
                            boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                            height: '35px',
                            paddingInlineStart: '8px',
                            fontWeight: '500',
                            color: '#212121',
                          }}
                          placeholder='Enter last name'
                          error={errors.lastname}
                          touched={touched.lastname}
                          disabled={isSubmitting}
                        />
                      </Flex>

                      {/* Email Address and Job Title Row */}
                      <Flex
                        alignItems={'flex-start'}
                        gap='5'
                        width={'100%'}
                        flexDir={isSmallLaptop ? 'column' : 'row'}
                        mt='.7rem'
                      >
                        <FormField
                          label='Email address'
                          name='username'
                          type='email'
                          labelStyles={{
                            fontSize: '12px',
                            color: '#82736C',
                            fontWeight: '600',
                          }}
                          inputStyles={{
                            maxWidth: '100%',
                            boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                            height: '35px',
                            paddingInlineStart: '8px',
                            fontWeight: '500',
                            color: '#212121',
                          }}
                          placeholder='name@email.com'
                          error={errors.username}
                          touched={touched.username}
                          disabled={isSubmitting}
                        />
                        <FormField
                          label='Job title'
                          name='job_role'
                          type='text'
                          labelStyles={{
                            fontSize: '12px',
                            color: '#82736C',
                            fontWeight: '600',
                          }}
                          inputStyles={{
                            maxWidth: '100%',
                            boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                            height: '35px',
                            paddingInlineStart: '8px',
                            fontWeight: '500',
                            color: '#212121',
                          }}
                          placeholder='e.g CEO'
                          error={errors.job_role}
                          touched={touched.job_role}
                          disabled={isSubmitting}
                        />
                      </Flex>

                      {/* Password Section */}
                      <Box mt='.7rem !important' width='100%'>
                        <Text fontSize='12px' color='#82736C' fontWeight='600' mb='2'>
                          Password
                        </Text>
                        <Button
                          h='35px'
                          w='auto'
                          borderRadius='10px'
                          fontWeight={400}
                          fontSize='15px'
                          textTransform='capitalize'
                          color='#6B5FC6'
                          bg='transparent'
                          boxShadow='0px 0px 0px 1.5px #8B8AAE'
                          padding='0 12px'
                          leftIcon={<Lock width={4} height={6} color='#6B5FC6' />}
                          onClick={() => setPasswordChangeVisibility(!isPasswordChangeVisible)}
                          _hover={{ bg: 'transparent' }}
                          _active={{ bg: 'transparent' }}
                        >
                          Change Password
                        </Button>
                      </Box>

                      {/* Password Change Fields */}
                      {isPasswordChangeVisible && (
                        <Box width='100%'>
                          <FormField
                            label='Current password'
                            name='current_password'
                            type='password'
                            labelStyles={{
                              fontSize: '15px',
                              color: '#212121',
                              fontWeight: 'bold',
                            }}
                            inputStyles={{
                              maxWidth: '100%',
                              boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                              height: '35px',
                              paddingInlineStart: '8px',
                            }}
                            placeholder='********'
                            error={errors.current_password}
                            touched={touched.current_password}
                            disabled={isSubmitting}
                          />
                          <Flex
                            alignItems={'flex-start'}
                            gap='5'
                            width={'100%'}
                            flexDir={isSmallLaptop ? 'column' : 'row'}
                            mt='2'
                          >
                            <FormField
                              label='New Password'
                              name='new_password'
                              type='password'
                              labelStyles={{
                                fontSize: '15px',
                                color: '#212121',
                                fontWeight: 'bold',
                              }}
                              inputStyles={{
                                maxWidth: '100%',
                                boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                                height: '35px',
                                paddingInlineStart: '8px',
                              }}
                              placeholder='********'
                              error={errors.new_password}
                              touched={touched.new_password}
                              disabled={isSubmitting}
                            />
                            <FormField
                              label='Confirm new Password'
                              name='confirmPassword'
                              type='password'
                              labelStyles={{
                                fontSize: '15px',
                                color: '#212121',
                                fontWeight: 'bold',
                              }}
                              inputStyles={{
                                maxWidth: '100%',
                                boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                                height: '35px',
                                paddingInlineStart: '8px',
                              }}
                              placeholder='********'
                              error={errors.confirmPassword}
                              touched={touched.confirmPassword}
                              disabled={isSubmitting}
                            />
                          </Flex>
                          <Box mt='2'>
                            {passwordRules.map((rule) => (
                              <Text
                                key={rule.label}
                                fontSize='12px'
                                fontWeight='500'
                                color={
                                  rule.isMet
                                    ? '#2E7D32'
                                    : showUnmetPasswordRulesInRed
                                    ? '#D32F2F'
                                    : '#82736C'
                                }
                              >
                                {rule.isMet ? '✓' : '○'} {rule.label}
                              </Text>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                </Box>

                {/* Footer Section */}
                <Flex width={'100%'} gap='4' pt='4' borderTop='1px solid #E5E5E5'>
                  <Button
                    flex='1'
                    h='40px'
                    borderRadius='10px'
                    fontSize='15px'
                    color='#212121'
                    bgColor='#F1EEFF'
                    px='6'
                    onClick={handleClose}
                    _hover={{ bgColor: '#EFEEFD' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    _hover={{ _disabled: {} }}
                    disabled={imageLoading || Object.keys(errors).length > 0 || isSubmitting}
                    type='submit'
                    flex='1'
                    h='40px'
                    borderRadius='10px'
                    fontSize='15px'
                    bgColor='#6B5FC6'
                    color='white'
                    px='6'
                    isLoading={isSubmitting}
                    loadingText='Saving...'
                  >
                    Save Changes
                  </Button>
                </Flex>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Flex>
  );
};

const stateToProperty = (state: any) => {
  return {
    userData: state.userSlice.userData,
  };
};

const mapDispatchToProperties = (dispatch: any) => {
  return {
    getUserDetails: (data: any) => dispatch(FetchUserDetails(data)),
    setUserModalVisible: (data: boolean) => dispatch(SetUserModalVisible(data)),
    updateUserDetail: (data: SignUpUser) => dispatch(UpdateUserDetails(data)),
    setUserTabs: (tab: number) => dispatch(SetTabs(tab)),
  };
};

export default connect(stateToProperty, mapDispatchToProperties)(ProfileFormNew);
